import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";

import ModalSkeleton from "../../../Components/Skeleton/modal-skeleton";
import PrimaryButton from "../../../Components/Buttons/primary-button";

import renderInstantTransferIneligible from "../Cards/instant-transfer-ineligible";

import useConnectBankAccount from "../../../CustomHooks/useConnectBankAccount";
import useAstra from "../../../CustomHooks/useAstra";

import { useModalContext } from "../../../Contexts/ModalContext";

import { useDeals } from "../../../CustomHooks/Queries";

import {
  isSelectedTransferMethodBank,
  isSelectedTransferMethodInstant,
} from "../../../Utils/helpers";
import { modalVariants } from "../../../Utils/constants";

import { BankIcon, AstraSmallIcon } from "../../../Assets/Images";
import { WarningTriangle } from "../../../Assets/Icons";

export default function renderAddPaymentEntity(modalContext, transferType) {
  const { updateModalComponent, pushModalStack, setModalTitle, setModalVariant } = modalContext;

  updateModalComponent(() => <AddPaymentEntity transferType={transferType} />);
  pushModalStack(() => renderAddPaymentEntity(modalContext, transferType));
  setModalTitle("Select Payment Method");
  setModalVariant(modalVariants.formDialog);
}

const AddPaymentEntity = ({ transferType }) => {
  const navigate = useNavigate();

  const modalContext = useModalContext();

  const { onModalClose, setModalClassName } = modalContext;

  const { isSavingAccount, plaidReady, openPlaid } = useConnectBankAccount({
    onSuccess: () => {
      navigate("/connected-accounts");
      onModalClose();
    },
    onError: onModalClose,
    onInitiatingSave: () => setModalClassName(null),
    onExit: () => setModalClassName(null),
  });

  const AddBank = () => {
    if (!isSavingAccount && plaidReady) {
      setModalClassName("d-none");
      openPlaid();
    }
  };

  const {
    isCreatingUserIntent: isAstraCreatingUserIntent,
    isLoadingRoutesCapabilities,
    isCreatingTrustedAuthenticationToken,
    isInstantTransferIneligible,
    openAstraPortal,
  } = useAstra();

  const handleProceed = (selectedTransferMethod) => {
    if (isSelectedTransferMethodInstant(selectedTransferMethod) && isInstantTransferIneligible) {
      renderInstantTransferIneligible(modalContext);
    } else if (isSelectedTransferMethodBank(selectedTransferMethod)) {
      AddBank();
    } else if (isSelectedTransferMethodInstant(selectedTransferMethod)) {
      onModalClose();
      openAstraPortal();
    }
  };

  const handleSkip = () => {
    onModalClose();
  };

  const isSettingUpAstra =
    isAstraCreatingUserIntent || isLoadingRoutesCapabilities || isCreatingTrustedAuthenticationToken;

  return (
    <SelectPaymentEntity
      infoAlertMessage={`Choose a payment method to complete your ${transferType}`}
      isInstantTransferIneligible={isInstantTransferIneligible}
      isProceeding={isSettingUpAstra || isSavingAccount || !plaidReady}
      handleProceed={handleProceed}
      handleSkip={handleSkip}
    />
  );
};

export const SelectPaymentEntity = ({
  infoAlertMessage,
  isInstantTransferIneligible,
  isProceeding,
  handleProceed,
  handleSkip,
}) => {
  const [selectedTransferMethod, setSelectedTransferMethod] = useState("ach");

  const { isLoading: isLoadingDeals, data: deals } = useDeals();

  return isLoadingDeals ? (
    <ModalSkeleton />
  ) : (
    <>
      <div className="modal-dialog-content">
        <p className="info-alert mb-4">
          <WarningTriangle />
          {infoAlertMessage}
        </p>
        <div className="funding-instrument-options">
          <button
            className={`funding-instrument-option ${
              isSelectedTransferMethodBank(selectedTransferMethod) ? "selected" : ""
            }`}
            onClick={setSelectedTransferMethod.bind(this, "ach")}
          >
            <div className="funding-instrument">
              <div className="funding-instrument-icon-wrapper">
                <BankIcon className="funding-instrument-icon" />
              </div>
              <div className="funding-instrument-details">
                <h4 className="funding-instrument-title">Standard transfer</h4>
                <span className="funding-instrument-description">1-3 business days. Always free</span>
              </div>
            </div>
            <div
              className={`custom-radio-button  ${
                isSelectedTransferMethodBank(selectedTransferMethod) ? "checked" : ""
              }`}
            ></div>
          </button>
          {deals?.data?.data?.currentDeal?.enabled_for_instant_transfer__c && (
            <button
              className={`funding-instrument-option ${
                isSelectedTransferMethodInstant(selectedTransferMethod) ? "selected" : ""
              }`}
              onClick={setSelectedTransferMethod.bind(this, "instant")}
            >
              <div className="funding-instrument">
                <div className="funding-instrument-icon-wrapper">
                  <AstraSmallIcon className="funding-instrument-icon" />
                </div>
                <div className="funding-instrument-details">
                  <h4 className="funding-instrument-title">Instant Transfer with Astra</h4>
                  <span className="funding-instrument-description">In a few minutes. 1.75% fee</span>
                </div>
              </div>
              <div
                className={`custom-radio-button  ${
                  isSelectedTransferMethodInstant(selectedTransferMethod) ? "checked" : ""
                }`}
              ></div>
              {isInstantTransferIneligible && <span className="restricted-badge">Restricted</span>}
            </button>
          )}
        </div>
      </div>
      <div className="action-container split-equally">
        <PrimaryButton onClick={() => handleProceed(selectedTransferMethod)} disabled={isProceeding}>
          Proceed {isProceeding && <CircularProgress />}
        </PrimaryButton>
        <PrimaryButton className="secondary-colors" onClick={handleSkip}>
          Skip for Now
        </PrimaryButton>
      </div>
    </>
  );
};
